import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Card, CardContent, Container, Divider, Grid2,
  MenuItem, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import Navbar from '../../setup/Navbar';
import BakerySaleBuilder from './BakerySaleBuilder';
import { getForwardInvoice, getUnpaidForwardInvoices, payForwardInvoice } from '../../../services/bakeryPosApi';

export default function BakeryCashCounter() {
  const { user } = useSelector((state) => state.auth);
  const [tab, setTab] = useState(0);
  const [lookup, setLookup] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [pending, setPending] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [billingLink, setBillingLink] = useState(null);
  const [taxModeEnabled, setTaxModeEnabled] = useState(false);
  const barcodeInputRef = useRef(null);
  const invoiceRef = useRef(null);
  const taxRate = taxModeEnabled ? Number(billingLink?.taxRate || 0) : 0;
  const forwardBaseTotal = Number(invoice?.grandTotal || 0);
  const forwardTaxAmount = Number((forwardBaseTotal * taxRate / 100).toFixed(2));
  const forwardPayable = Number((forwardBaseTotal + forwardTaxAmount).toFixed(2));

  useEffect(() => { invoiceRef.current = invoice; }, [invoice]);

  const activeBranchID = user?.defaultBranchID || user?.branchID || null;
  const refreshPending = useCallback(async () => {
    if (!activeBranchID) {
      setPending([]);
      return;
    }
    try {
      setPending(await getUnpaidForwardInvoices(activeBranchID, user?.userID));
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Pending forward invoices load nahi ho sakin.');
    }
  }, [activeBranchID, user?.userID]);

  useEffect(() => {
    if (tab === 0) refreshPending();
  }, [tab, refreshPending]);

  useEffect(() => {
    if (!activeBranchID) return undefined;
    const eventsUrl = `${global.SET_API_URL}bakeryPos/events?branchID=${encodeURIComponent(activeBranchID)}`;
    const events = new EventSource(eventsUrl);
    const refreshFromEvent = () => refreshPending();
    const refreshUpdatedInvoice = async (event) => {
      refreshPending();
      try {
        const updated = JSON.parse(event.data || '{}');
        if (!updated.tokenNo) return;
        if (invoiceRef.current && Number(invoiceRef.current.forwardInvoiceID) === Number(updated.forwardInvoiceID)) {
          setInvoice(await getForwardInvoice(updated.tokenNo));
        }
      } catch (_error) {
        // Pending-list refresh remains authoritative if event payload is malformed.
      }
    };
    const removeDiscardedInvoice = (event) => {
      try {
        const discarded = JSON.parse(event.data || '{}');
        setPending((current) => current.filter((row) => Number(row.forwardInvoiceID) !== Number(discarded.forwardInvoiceID)));
        setInvoice((current) => {
          const isDiscarded = current && (
            Number(current.forwardInvoiceID) === Number(discarded.forwardInvoiceID)
            || String(current.tokenNo) === String(discarded.tokenNo)
          );
          if (isDiscarded) {
            setLookup('');
            setMessage('Forward invoice ' + (discarded.tokenNo || '') + ' salesman ne discard kar di.');
            return null;
          }
          return current;
        });
      } catch (_error) {
        // A refresh still guarantees that the server-side unpaid list is authoritative.
      }
      refreshPending();
    };
    events.addEventListener('forward-invoice-created', refreshFromEvent);
    events.addEventListener('forward-invoice-updated', refreshUpdatedInvoice);
    events.addEventListener('forward-invoice-paid', refreshFromEvent);
    events.addEventListener('forward-invoice-cancelled', removeDiscardedInvoice);
    const fallbackRefresh = window.setInterval(refreshPending, 15000);
    return () => {
      window.clearInterval(fallbackRefresh);
      events.close();
    };
  }, [activeBranchID, refreshPending]);
  useEffect(() => {
    if (!user?.userID) return;
    setTaxModeEnabled(false);
    axios.get(global.SET_API_URL + 'billingIdentities/current/' + user.userID)
      .then((response) => setBillingLink(response.data?.data || null))
      .catch(() => setBillingLink(null));
  }, [user?.userID]);
  useEffect(() => {
    const handleKey = (event) => {
      if (!event.ctrlKey || String(event.key).toLowerCase() !== 'm' || busy) return;
      event.preventDefault();
      setTaxModeEnabled((current) => !current);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [billingLink, busy]);

  const scan = async (value = lookup) => {
    if (!String(value).trim()) return;
    setBusy(true); setMessage('');
    try { setInvoice(await getForwardInvoice(String(value).trim())); }
    catch (error) { setInvoice(null); setMessage(error?.response?.data?.message || 'Invoice nahi mila.'); }
    finally { setBusy(false); }
  };
  const receivePayment = async (selectedMethod = paymentMethod) => {
    if (busy || !invoice || invoice.status !== 'unpaid') return;
    setPaymentMethod(selectedMethod);
    setBusy(true); setMessage('');
    try {
      const result = await payForwardInvoice(invoice.tokenNo, {
        cashierUserID: user.userID,
        paymentMethod: selectedMethod,
        loggedInUserID: user.userID,
        billingUserID: user.userID,
        taxModeEnabled,
        taxAuthority: taxModeEnabled ? billingLink?.taxAuthority : null,
        taxRate,
        billingIdentityLinkID: billingLink?.linkID || null,
        taxProfileID: taxModeEnabled ? billingLink?.taxProfileID : null,
      });
      setMessage(
        result.print?.success
          ? `Payment complete. POS Order #${result.data.orderID}. Bill ${result.print.counterName || 'cash counter'} par print ho gaya.`
          : `Payment complete. POS Order #${result.data.orderID}. Bill print issue: ${result.print?.message || 'Printer unavailable'}`
      );
      setInvoice({ ...invoice, status: 'paid', posOrderID: result.data.orderID });
      setLookup('');
      window.requestAnimationFrame(() => barcodeInputRef.current?.focus());
      refreshPending();
    } catch (error) {
      setMessage(error?.response?.data?.message || 'Payment complete nahi ho saki.');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const shortcutMethods = { F6: 'cash', F7: 'card', F8: 'wallet', F9: 'bank' };
    const handlePaymentShortcut = (event) => {
      const selectedMethod = shortcutMethods[event.key];
      if (!selectedMethod || event.repeat) return;
      event.preventDefault();
      if (!invoice || invoice.status !== 'unpaid') {
        setMessage('Payment ke liye pehle unpaid invoice load karein.');
        return;
      }
      receivePayment(selectedMethod);
    };
    window.addEventListener('keydown', handlePaymentShortcut);
    return () => window.removeEventListener('keydown', handlePaymentShortcut);
  }, [invoice, busy, taxModeEnabled, billingLink]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" fontWeight={800}>Bakery Cash Counter</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography fontWeight={700} color="primary">F6 Cash ? F7 Card ? F8 Wallet ? F9 Bank Transfer</Typography>
            <Box title={taxModeEnabled ? ((billingLink?.taxAuthority || 'Tax') + ' mode') : 'Normal / non-GST mode'} sx={{ width:14,height:14,borderRadius:'50%',bgcolor:taxModeEnabled?'#ffb300':'#b0bec5',border:'2px solid',borderColor:taxModeEnabled?'#b71c1c':'#78909c',boxShadow:taxModeEnabled?'0 0 8px 2px rgba(255,179,0,.8)':'none',animation:taxModeEnabled?'gstModeBlink 0.8s ease-in-out infinite alternate':'none','@keyframes gstModeBlink':{from:{opacity:.35,transform:'scale(.85)'},to:{opacity:1,transform:'scale(1.2)'}} }}/>
          </Stack>
        </Stack>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label="Scan Forward Invoice" />
          <Tab label="New Direct Sale" />
        </Tabs>
        {tab === 1 ? <BakerySaleBuilder mode="direct" billingContext={{ billingLink, activeBillingUserID: user?.userID, taxModeEnabled, taxRate }} /> : (
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, md: 7 }}>
              <Card variant="outlined"><CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    autoFocus fullWidth label="Scan QR / Enter Token Number" value={lookup}
                    inputRef={barcodeInputRef}
                    onChange={(e) => setLookup(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') scan(); }}
                  />
                  <Button variant="contained" disabled={busy} onClick={() => scan()}>Load Invoice</Button>
                </Stack>
                {message && <Alert severity={message.includes('complete') ? 'success' : 'info'} sx={{ mt: 2 }}>{message}</Alert>}
                {invoice && (
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="h5" fontWeight={800}>{invoice.tokenNo}</Typography>
                      <Typography fontWeight={800}>{String(invoice.status).toUpperCase()}</Typography>
                    </Stack>
                    <Typography>{invoice.customerName || 'Walk-in Customer'} {invoice.customerPhone ? ` ${invoice.customerPhone}` : ''}</Typography>
                    <Divider sx={{ my: 1 }} />
                    {invoice.items.map((item) => (
                      <Stack key={item.forwardInvoiceItemID} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography>{item.itemName} � {Number(item.quantity)}</Typography>
                        <Typography>Rs. {Number(item.lineTotal).toLocaleString()}</Typography>
                      </Stack>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    {taxModeEnabled && <Stack direction="row" justifyContent="space-between"><Typography>{billingLink?.taxAuthority} Tax ({taxRate}%)</Typography><Typography>Rs. {forwardTaxAmount.toLocaleString()}</Typography></Stack>}
                    <Stack direction="row" justifyContent="space-between"><Typography variant="h6" fontWeight={800}>Payable</Typography><Typography variant="h6" fontWeight={800}>Rs. {forwardPayable.toLocaleString()}</Typography></Stack>
                    {invoice.status === 'unpaid' && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                        <TextField select fullWidth label="Payment Method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                          {['cash', 'card', 'wallet', 'bank', 'voucher'].map((method) => <MenuItem key={method} value={method}>{method.toUpperCase()}</MenuItem>)}
                        </TextField>
                        <Button variant="contained" color="success" disabled={busy} onClick={() => receivePayment()}>Receive Payment</Button>
                      </Stack>
                    )}
                  </Box>
                )}
              </CardContent></Card>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 5 }}>
              <Card variant="outlined"><CardContent>
                <Typography variant="h6" fontWeight={700}>Pending Forward Invoices</Typography>
                <Stack spacing={1} sx={{ mt: 1, maxHeight: 520, overflowY: 'auto' }}>
                  {pending.map((row) => (
                    <Button key={row.forwardInvoiceID} variant="outlined" sx={{ justifyContent: 'space-between' }} onClick={() => { setLookup(row.tokenNo); scan(row.tokenNo); }}>
                      <span>{row.tokenNo}</span><span>Rs. {Number(row.grandTotal).toLocaleString()}</span>
                    </Button>
                  ))}
                  {!pending.length && <Typography color="text.secondary">Koi pending invoice nahi.</Typography>}
                </Stack>
              </CardContent></Card>
            </Grid2>
          </Grid2>
        )}
      </Container>
    </>
  );
}
